import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MenuVisibilityContext } from "../context/MenuVisibilityContext";
import { SidebarStateContext } from "../context/SidebarStateContext";
import { api } from "../lib/api";
import { subscribeMenuVisibility } from "../lib/menuVisibilityBus";
import BadgeRail from "./BadgeRail";
import AccountAssistantWidget from "./warren/AssistantWidget";
import FeedbackWidget from "./warren/FeedbackWidget";
import GuidedTour from "./GuidedTour";
import TourPickerOverlay from "./TourPickerOverlay";
import CommunityProfileOnboardingModal from "./CommunityProfileOnboardingModal";
import AccountMenu from "./AccountMenu";
import NotificationsBell from "./NotificationsBell";
import CommunityButton from "./CommunityButton";
import ReadingLevelPill from "./ReadingLevelPill";
import GlobalSearch from "./GlobalSearch";
import { TOOLS } from "../pages/Tools";
import { AI_TOOLS } from "../pages/AICenter";
import {
  LayoutDashboard, GraduationCap, BookOpen, Award, Calculator, CalendarClock,
  Folder, Database, Settings, LogOut, Search, HelpCircle,
  ChevronDown, ChevronRight, ChevronLeft, Lock, FileCheck, Trophy, Sparkles, Users, Menu, Compass, Tag,
  UserSquare2, BellRing, Megaphone, ClipboardList, Gavel,
  PanelLeftClose, PanelLeftOpen, FlaskConical, MessageSquarePlus, Zap,
  Scale, Monitor, Smartphone, Tablet,
} from "lucide-react";

// Item registry — icons + routes live in code because they map to real
// features. Labels + section + order come from the backend layout doc so
// admins can rename / reorder / move items between sections at runtime.
// The `kind` field routes each item to the right renderer below.
const ITEM_REGISTRY = {
  "nav-welcome":            { icon: Sparkles,       to: "/welcome",            kind: "simple",    defaultLabel: "Welcome" },
  "nav-walkthrough":        { icon: Compass,        to: "/walkthrough",        kind: "simple",    defaultLabel: "Platform Walkthrough" },
  "nav-dashboard":          { icon: LayoutDashboard, to: "/dashboard",         kind: "simple",    defaultLabel: "Dashboard", end: true },
  "nav-course":             { icon: GraduationCap,  to: "/course",             kind: "course",    defaultLabel: "Course Overview" },
  "nav-modules":            { icon: BookOpen,       to: null,                  kind: "modules",   defaultLabel: "Course Modules" },
  "nav-workbook":           { icon: ClipboardList,  to: "/workbook",           kind: "simple",    defaultLabel: "Course Workbook" },
  "nav-decisions":          { icon: Gavel,          to: "/decisions",          kind: "simple",    defaultLabel: "Your Decisions" },
  "nav-progress":           { icon: Award,          to: "/progress",           kind: "simple",    defaultLabel: "Progress & Certificates" },
  "nav-final-exam":         { icon: Trophy,         to: "/exam/final",         kind: "simple",    defaultLabel: "Final Certification Exam" },
  "nav-tools":              { icon: Calculator,     to: null,                  kind: "tools",     defaultLabel: "Financial Tools" },
  "nav-blueprint":          { icon: FileCheck,      to: "/blueprint",          kind: "simple",    defaultLabel: "Launch Blueprint" },
  // `nav-planner` is back in the registry so the Menu Directory CMS can
  // toggle it — visibility is now managed centrally there, not hardcoded.
  "nav-planner":            { icon: CalendarClock,  to: "/planner",            kind: "simple",    defaultLabel: "Marketing Planner" },
  "nav-projects":           { icon: Folder,         to: "/projects",           kind: "simple",    defaultLabel: "Projects" },
  "nav-ai-center":          { icon: Sparkles,       to: "/ai-lab",             kind: "ai-studio", defaultLabel: "CPG AI Lab" },
  // Phase-6 additive-only sibling to nav-ai-center. Uses `kind: "simple"`
  // (no dropdown) and points at the new /ai-cpg-tools route. The legacy
  // CPG AI Lab entry above is deliberately untouched.
  "nav-ai-cpg-tools":       { icon: FlaskConical,   to: "/ai-cpg-tools/brand-profile", kind: "simple",    defaultLabel: "AI CPG Tools" },
  // Phase-6 additive-only entry for CPG Agreements. Same section as its
  // two siblings above; kind:"simple" so it renders as a flat nav row.
  "nav-agreements":         { icon: Scale,          to: "/agreements",         kind: "simple",    defaultLabel: "CPG Agreements" },
  "nav-members":            { icon: Users,          to: "/members",            kind: "simple",    defaultLabel: "Member Directory" },
  "nav-industry-directory": { icon: Compass,        to: "/industry-directory", kind: "simple",    defaultLabel: "Industry Directory" },
  "nav-deals":              { icon: Tag,            to: "/deals",              kind: "simple",    defaultLabel: "CPG Deals" },
  "nav-databases":          { icon: Database,       to: null,                  kind: "databases", defaultLabel: "Databases" },
  "nav-community-profile":  { icon: UserSquare2,    to: "/community-profile",  kind: "simple",    defaultLabel: "Community profile" },
  "nav-buyer-alerts":       { icon: BellRing,       to: "/buyer-alerts",       kind: "simple",    defaultLabel: "Retail buyer alerts" },
  "nav-story-wall":         { icon: Megaphone,      to: "/story-wall",         kind: "simple",    defaultLabel: "Founder Story Wall" },
  "nav-account":            { icon: Settings,       to: "/account",            kind: "simple",    defaultLabel: "Account & Access" },
  // Tester-only automation console. Kept out of the backend menu-directory
  // doc — it's injected client-side for role='tester' / super_admin only,
  // so members never see or fetch this item (see `itemsBySection` below).
  "nav-automation":         { icon: Zap,            to: "/automation",         kind: "simple",    defaultLabel: "Automation" },
};

// Fallback layout used before the /menu-visibility fetch resolves so we
// don't flash an empty sidebar on first paint.
const BASELINE_SECTIONS_FALLBACK = [
  { id: "section-overview",    label: "Overview",    order: 0 },
  { id: "section-learn",       label: "Learn",       order: 1 },
  { id: "section-build",       label: "Build",       order: 2 },
  { id: "section-resources",   label: "Resources",   order: 3 },
  { id: "section-connect",     label: "Connect",     order: 4 },
  { id: "section-my-presence", label: "My Presence", order: 5 },
  { id: "section-settings",    label: "Settings",    order: 6 },
];

const BASELINE_ITEMS_FALLBACK = Object.entries(ITEM_REGISTRY).map(([id, r], idx) => ({
  id,
  label: r.defaultLabel,
  section: {
    "nav-welcome": "section-overview", "nav-walkthrough": "section-overview", "nav-dashboard": "section-overview",
    "nav-course": "section-learn", "nav-modules": "section-learn", "nav-workbook": "section-learn",
    "nav-decisions": "section-learn", "nav-progress": "section-learn", "nav-final-exam": "section-learn",
    "nav-tools": "section-build", "nav-blueprint": "section-build", "nav-planner": "section-build", "nav-projects": "section-build",
    "nav-ai-center": "section-resources", "nav-ai-cpg-tools": "section-resources", "nav-agreements": "section-resources", "nav-members": "section-resources",
    "nav-industry-directory": "section-resources", "nav-deals": "section-resources",
    "nav-databases": "section-connect",
    "nav-community-profile": "section-my-presence", "nav-buyer-alerts": "section-my-presence", "nav-story-wall": "section-my-presence",
    "nav-account": "section-settings",
  }[id],
  order: idx,
  kind: r.kind,
}));

const READ_CACHED_LAYOUT = () => {
  try {
    const raw = window.sessionStorage.getItem("cpg.menu.layout");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.sections || !parsed?.items) return null;
    return parsed;
  } catch { return null; }
};


/**
 * Reactive viewport bucket — "mobile" (<768px), "tablet" (768–1023px),
 * or "desktop" (>=1024px). Aligned exactly with Tailwind's default `md`
 * (768) and `lg` (1024) breakpoints so 1024px is desktop and the sidebar
 * stays docked when the desktop grid takes over.
 * Falls back to "desktop" during SSR / non-matchMedia environments so
 * the sidebar never over-restricts.
 */
function useViewport() {
  const compute = () => {
    if (typeof window === "undefined" || !window.matchMedia) return "desktop";
    // Desktop = ≥1280 px (Tailwind `xl`). Below that the sidebar is
    // ALWAYS off-canvas overlay (opened by the hamburger); there is
    // no intermediate icon-rail state <1280. This matches the
    // TailAdmin reference pattern exactly.
    if (window.matchMedia("(min-width: 1280px)").matches) return "desktop";
    if (window.matchMedia("(min-width: 768px)").matches)  return "tablet";
    return "mobile";
  };
  const [vp, setVp] = React.useState(compute);
  React.useEffect(() => {
    const mqMd = window.matchMedia("(min-width: 768px)");
    const mqXl = window.matchMedia("(min-width: 1280px)");
    const handler = () => setVp(compute());
    mqMd.addEventListener("change", handler);
    mqXl.addEventListener("change", handler);
    return () => {
      mqMd.removeEventListener("change", handler);
      mqXl.removeEventListener("change", handler);
    };
  }, []);
  return vp;
}


/**
 * True when the item is EXPLICITLY switched off on the given viewport.
 * Missing/legacy items keep their permissive defaults (all viewports
 * = true) so a stale layout never silently hides an item.
 */
function viewportGated(item, vp) {
  const vps = item?.viewports;
  if (!vps || typeof vps !== "object") return false;
  return vps[vp] === false;
}

/**
 * Prefix map for `nav-*` items whose sidebar entry is a dropdown
 * (`to: null` in ITEM_REGISTRY). Both the bare parent URL
 * (`/databases`) and every child URL (`/databases/retailers`) must
 * resolve to the parent nav item so viewport gating applies to the
 * whole subtree — otherwise an admin could ban "Databases" on mobile
 * and users would still reach `/databases` (or `/databases/retailers`)
 * by URL.
 */
const NAV_ROUTE_PREFIXES = {
  "nav-modules":    ["/modules", "/module", "/course/module"],
  "nav-tools":      ["/tools", "/tool"],
  "nav-databases":  ["/databases", "/database"],
  "nav-ai-center":  ["/ai-lab", "/ai-studio", "/ai-center"],
  "nav-agreements": ["/agreements"],
  "nav-ai-cpg-tools": ["/ai-cpg-tools"],
};

/**
 * Resolve which sidebar item "owns" the given pathname. Exact `to:`
 * match wins; otherwise we fall back to the prefix map above. Prefix
 * matches accept both the bare base (`/databases`) and any child
 * (`/databases/retailers`) — trailing-slash agnostic — so a bare
 * dropdown parent URL can never leak through the guard.
 * Returns the item object (with .viewports) or null.
 */
function findOwningItemForPath(pathname, items) {
  if (!pathname || !Array.isArray(items) || items.length === 0) return null;
  let best = null;
  let bestLen = -1;
  // Exact match on `to` first.
  for (const it of items) {
    const reg = ITEM_REGISTRY[it.id];
    if (!reg?.to) continue;
    if (pathname === reg.to || pathname.startsWith(reg.to + "/")) {
      if (reg.to.length > bestLen) { best = it; bestLen = reg.to.length; }
    }
  }
  if (best) return best;
  // Prefix map for dropdown-only parents — accept bare + descendants.
  for (const it of items) {
    const prefixes = NAV_ROUTE_PREFIXES[it.id];
    if (!prefixes) continue;
    for (const raw of prefixes) {
      const base = raw.endsWith("/") ? raw.slice(0, -1) : raw;
      if (pathname === base || pathname.startsWith(base + "/")) {
        if (base.length > bestLen) { best = it; bestLen = base.length; }
      }
    }
  }
  return best;
}

export default function AppShell({ children, title }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [dbs, setDbs] = useState([]);
  const [modules, setModules] = useState([]);
  const [certs, setCerts] = useState([]);
  const [progress, setProgress] = useState(null);
  const [dbsOpen, setDbsOpen] = useState(false);
  const [modsOpen, setModsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Mobile-only search overlay — when true, the header expands its
  // second row to reveal GlobalSearch. Tapping the magnifier toggles it
  // and Escape / blur closes it. From md+ (≥768 px) the search fits
  // inline in the top row so no overlay is needed.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [layout, setLayout] = useState(() =>
    READ_CACHED_LAYOUT() || {
      sections: BASELINE_SECTIONS_FALLBACK,
      items: BASELINE_ITEMS_FALLBACK,
      disabled: [],
    }
  );
  const disabledSet = useMemo(() => new Set(layout.disabled || []), [layout.disabled]);
  const comingSoonSet = useMemo(() => new Set(layout.coming_soon || []), [layout.coming_soon]);
  const isNewSet = useMemo(() => new Set(layout.is_new || []), [layout.is_new]);
  const planGatedSet = useMemo(() => new Set(layout.plan_gated || []), [layout.plan_gated]);
  const pageElementsDisabledSet = useMemo(
    () => new Set(layout.page_elements_disabled || []),
    [layout.page_elements_disabled],
  );
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = window.localStorage.getItem("cpg.sidebar.collapsed");
      // Explicit user preference wins at every reload (desktop only —
      // tablet always renders the collapsed rail as its persistent
      // baseline, using `tabletOverlayOpen` for the expand affordance).
      if (stored === "1") return true;
      if (stored === "0") return false;
      // First-run default: expanded on desktop, collapsed on tablet.
      if (typeof window !== "undefined" && window.matchMedia) {
        return window.matchMedia("(max-width: 1279px)").matches;
      }
      return false;
    } catch { return false; }
  });

  // Tablet-only transient state (768-1279): when true, the collapsed
  // rail visually expands to the full 288 px sidebar as an OVERLAY on
  // top of the main content — with a scrim behind. Content underneath
  // does NOT reflow (padding stays at rail width). Toggled by the
  // in-sidebar collapse/expand button at this viewport range.
  const [tabletOverlayOpen, setTabletOverlayOpen] = useState(false);
  const toggleCollapsed = () => {
    // At tablet (768-1279): the sidebar's persistent baseline is the
    // collapsed rail (see localStorage default via matchMedia). The
    // in-rail collapse/expand button toggles a TRANSIENT overlay
    // instead of resizing the docked width — content underneath does
    // not reflow. At desktop: same button toggles the docked width
    // and reflows main content (existing push/reflow behavior).
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 768px) and (max-width: 1279px)").matches) {
      setTabletOverlayOpen((v) => !v);
      return;
    }
    setCollapsed((v) => {
      const nv = !v;
      try { window.localStorage.setItem("cpg.sidebar.collapsed", nv ? "1" : "0"); } catch {}
      return nv;
    });
  };
  const sidebarState = useMemo(() => ({
    collapsed, setCollapsed, toggleCollapsed,
    mobileNavOpen, setMobileNavOpen,
    tabletOverlayOpen, setTabletOverlayOpen,
  }), [collapsed, mobileNavOpen, tabletOverlayOpen]);
  const location = useLocation();

  // Header title auto-resolution — when a page doesn't pass `title`,
  // we look up the current pathname's owning nav item via the same
  // route resolver the viewport route guard uses and pull its label.
  // Handles both exact-`to` matches (e.g. `/agreements` → "CPG
  // Agreements") and dropdown-parent prefix matches (e.g. any child
  // under `/module/*`, `/ai-cpg-tools/*` resolves to the parent nav
  // item's label). Falls back to "Dashboard" when no owning item is
  // found (e.g. the actual `/dashboard` route).
  const resolvedTitle = useMemo(() => {
    if (title) return title;                       // page passed an override
    const owning = findOwningItemForPath(location.pathname, layout?.items || []);
    // Prefer the admin-editable label from the layout, then the
    // hardcoded registry default. `Dashboard` as the last fallback
    // keeps pre-existing behaviour for `/dashboard` itself.
    return owning?.label || ITEM_REGISTRY[owning?.id]?.defaultLabel || "Dashboard";
  }, [title, location.pathname, layout?.items]);

  const navScrollRef = useRef(null);
  useLayoutEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const stored = window.sessionStorage.getItem("cpg.sidebar.scroll");
    if (stored != null) el.scrollTop = parseInt(stored, 10) || 0;
  }, []);
  const handleNavScroll = (e) => {
    if (handleNavScroll._pending) return;
    handleNavScroll._pending = true;
    requestAnimationFrame(() => {
      handleNavScroll._pending = false;
      try { window.sessionStorage.setItem("cpg.sidebar.scroll", String(e.target.scrollTop)); }
      catch (_) { /* private mode etc — ignore */ }
    });
  };

  useEffect(() => {
    setMobileNavOpen(false);
    // Auto-collapse the tablet overlay on any route change so it
    // behaves like the mobile drawer / desktop overlay (item click
    // closes and returns to the icon rail baseline).
    setTabletOverlayOpen(false);
  }, [location.pathname]);

  // Reactive viewport bucket drives the sidebar's default state at
  // each breakpoint range:
  //   • Mobile (<768): sidebar fully hidden, hamburger opens overlay
  //   • Tablet + Desktop (≥768): persistent sidebar with manual
  //     collapse/expand toggle; default state at first load is
  //     collapsed for tablet, expanded for desktop (set at mount via
  //     matchMedia; user's subsequent choice persists via localStorage).
  const viewport = useViewport();

  // Close any leftover mobile overlay the moment we enter tablet or
  // desktop — from md+ the sidebar is persistent and the hamburger
  // is `md:hidden`, so a stray `mobileNavOpen=true` would render a
  // dangling scrim over the docked sidebar with no way to dismiss.
  useEffect(() => {
    if (viewport !== "mobile") setMobileNavOpen(false);
    // When leaving tablet in either direction, clear the transient
    // overlay so it doesn't leak into mobile drawer / desktop docked.
    if (viewport !== "tablet") setTabletOverlayOpen(false);
  }, [viewport]);

  // Effective "icon-only" flag for the sidebar visual system. On
  // tablet the sidebar shows the icon rail UNLESS the transient
  // overlay is open (then it renders the full labels on top of
  // content). On desktop the user's `collapsed` toggle is the source
  // of truth. Mobile drawer always shows full labels when open.
  const iconOnly =
    viewport === "mobile"
      ? false
      : viewport === "tablet"
        ? !tabletOverlayOpen
        : collapsed;

  // Fixed-position header: measure its rendered height and pipe it into
  // a CSS variable (`--header-h`) so the main region can pad itself by
  // exactly that much on every breakpoint. Using ResizeObserver keeps
  // it accurate through mobile → desktop resizes and mobile-search row
  // showing / hiding.
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const apply = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty("--header-h", `${h}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);

  useEffect(() => {
    const loadMods = () => api.get("/modules").then((r) => setModules(r.data)).catch(() => setModules([]));
    const loadCerts = () => api.get("/certifications").then((r) => setCerts(r.data)).catch(() => setCerts([]));
    const loadProgress = () => api.get("/progress/me").then((r) => setProgress(r.data)).catch(() => setProgress(null));
    // Refetch helper reused by the mount effect AND the
    // menu-directory bus so admin toggles apply live.
    const loadMenu = () => api.get("/menu-visibility").then((r) => {
      if (r.data?.sections && r.data?.items) {
        setLayout(r.data);
        try { window.sessionStorage.setItem("cpg.menu.layout", JSON.stringify(r.data)); }
        catch (_) { /* ignore */ }
      }
    }).catch(() => { /* keep cached / baseline */ });
    api.get("/databases").then((r) => setDbs(r.data)).catch(() => setDbs([]));
    loadMods();
    loadCerts();
    loadProgress();
    loadMenu();
    // Refresh the sidebar progress ring + module set when any child
    // route publishes a completion (see ModuleQuiz.jsx onPassed).
    const onProgress = () => { loadMods(); loadCerts(); loadProgress(); };
    window.addEventListener("cpg:module-progress", onProgress);
    // Live menu-directory sync — admin saves in any tab trigger a
    // re-fetch here so page-element toggles (e.g. hide the WhatsApp/SMS
    // channel on /account/notifications) apply without a page reload.
    const unsubMenu = subscribeMenuVisibility(loadMenu);
    return () => {
      window.removeEventListener("cpg:module-progress", onProgress);
      unsubMenu();
    };
  }, []);

  const passedSlugs = useMemo(() => new Set(progress?.passed_slugs || []), [progress]);
  const primaryEarned = !!(progress && progress.core_total > 0 && progress.core_completed >= progress.core_total);

  const doLogout = async () => { await logout(); nav("/"); };
  const initials = (user?.name || user?.email || "?").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const avatarUrl = user?.picture;
  // Default to "free" for the topbar chip so a brand-new signup whose
  // /me response hasn't populated package_slug yet doesn't render as
  // "NO PLAN" — every authenticated user is at least on Free (see the
  // startup backfill + /auth/register in backend for the source of truth).
  const pkgLabel = (user?.package_slug || "free").toUpperCase();

  // Group items by section, honouring order + enabled flags.
  const itemsBySection = useMemo(() => {
    const bySection = {};
    for (const item of (layout.items || [])) {
      if (disabledSet.has(item.id)) continue;
      if (item.id === "nav-automation") continue; // owned client-side
      if (!bySection[item.section]) bySection[item.section] = [];
      bySection[item.section].push(item);
    }
    // Tester-only Automation entry: injected here (not stored in the
    // backend menu-directory doc) so members never receive the id in
    // the layout payload and never render the item.
    const role = (user?.role || "").toLowerCase();
    if (role === "tester" || role === "super_admin") {
      const targetSection = "section-settings";
      if (!bySection[targetSection]) bySection[targetSection] = [];
      bySection[targetSection].push({
        id: "nav-automation",
        label: "Automation",
        section: targetSection,
        order: 999,
        kind: "simple",
      });
    }
    for (const arr of Object.values(bySection)) arr.sort((a, b) => a.order - b.order);
    return bySection;
  }, [layout.items, disabledSet, user?.role]);

  const visibleSections = useMemo(() => (
    (layout.sections || [])
      // Anything not tagged `location: "sidebar"` (i.e. Header + Profile Menu
      // sections that live in Admin Console → Menu Directory only) MUST NOT
      // render in the left rail. Sections without a `location` default to
      // sidebar so this stays fully backwards-compatible.
      .filter((s) => !s.location || s.location === "sidebar")
      .filter((s) => !disabledSet.has(s.id))
      .filter((s) => (itemsBySection[s.id] || []).length > 0)
      .sort((a, b) => a.order - b.order)
  ), [layout.sections, disabledSet, itemsBySection]);

  const rendererCtx = {
    dbs, modules, progress,
    dbsOpen, setDbsOpen,
    modsOpen, setModsOpen,
    toolsOpen, setToolsOpen,
    aiOpen, setAiOpen,
    childrenOverrides: layout.children_overrides || {},
    childrenAllowed: layout.children_allowed || {},
    // Plan-gate set — SidebarItem short-circuits ANY renderer type
    // (dropdown/course/simple) into the "Upgrade to Access" chip
    // when the item id appears here. Backed by Menu Directory
    // `plans` list; empty for admins.
    planGated: planGatedSet,
    viewport,
  };

  return (
    <MenuVisibilityContext.Provider value={{
      disabled: disabledSet,
      comingSoon: comingSoonSet,
      isNew: isNewSet,
      planGated: planGatedSet,
      pageElementsDisabled: pageElementsDisabledSet,
      // `children_allowed` (per-parent forbidden-child list) is fetched
      // as part of the same /menu-visibility payload the sidebar uses,
      // so we surface it through the same context. Pages under the
      // AppShell — e.g. Financial Tools Intro — read
      // `isChildPlanGated("nav-tools", tool.slug)` to swap "Open tool"
      // for the Upgrade CTA, keeping Menu Directory the single source
      // of truth for plan-based feature access.
      childrenAllowed: Object.fromEntries(
        Object.entries(layout.children_allowed || {}).map(
          ([parent, forbidden]) => [parent, new Set(forbidden || [])],
        ),
      ),
      // Full per-parent per-child overrides — surfaces `label` /
      // `order` (and every other patch admin sets) to any page inside
      // the shell. Course Workbook uses this to keep bonus-module
      // names and ordering in sync with the Menu Directory.
      childrenOverrides: layout.children_overrides || {},
      isDisabled: (id) => disabledSet.has(id),
      isComingSoon: (id) => comingSoonSet.has(id),
      hasNewBadge: (id) => isNewSet.has(id),
      isPageElementHidden: (id) => pageElementsDisabledSet.has(id),
      isPlanGated: (id) => planGatedSet.has(id),
      isChildPlanGated: (parent, child) => {
        const forbidden = (layout.children_allowed || {})[parent];
        return Array.isArray(forbidden) && forbidden.includes(child);
      },
    }}>
    <SidebarStateContext.Provider value={sidebarState}>
    <div className="min-h-screen w-full flex bg-shell text-shell-fg">
      {(mobileNavOpen || tabletOverlayOpen) && (
        <button
          aria-label="Close menu"
          onClick={() => { setMobileNavOpen(false); setTabletOverlayOpen(false); }}
          data-testid="app-sidebar-backdrop"
          // Scrim covers everything at <xl (mobile drawer + tablet
          // overlay). At ≥xl the sidebar is docked with push/reflow
          // and no overlay/scrim is used.
          className="xl:hidden fixed inset-0 z-40 bg-shell-side/60 backdrop-blur-sm"
        />
      )}
      <aside
        data-testid="app-sidebar"
        data-collapsed={iconOnly ? "true" : "false"}
        className={[
          "fixed inset-y-0 left-0 flex flex-col bg-shell-side text-shell-side-fg",
          "transition-[width,transform] duration-200",
          // ─── MOBILE (<768 px) ─────────────────────────────────────
          // Off-canvas drawer. Slide-in above scrim when mobileNavOpen.
          "w-72 max-w-[85vw] z-50 shadow-2xl",
          mobileNavOpen ? "translate-x-0" : "-translate-x-full",
          // ─── TABLET (768-1279 px) ─────────────────────────────────
          // Persistent icon-only rail (68 px) unless the transient
          // `tabletOverlayOpen` is true — then widens to 288 px as an
          // OVERLAY above the scrim (z-50). Content underneath keeps
          // its md:pl-[68px] padding so it never reflows.
          "md:translate-x-0",
          tabletOverlayOpen
            ? "md:w-72 md:z-50 md:shadow-2xl"
            : "md:w-[68px] md:z-30 md:shadow-none",
          // ─── DESKTOP (≥1280 px) ──────────────────────────────────
          // Docked width follows `collapsed`. `xl:` variants override
          // the tablet baseline in source order, so no overlay/scrim
          // interaction here — pure push/reflow.
          "xl:translate-x-0 xl:z-40 xl:shadow-none",
          collapsed ? "xl:w-[68px]" : "xl:w-72",
        ].join(" ")}
      >
        <div className="px-5 md:px-6 pt-5 md:pt-6 pb-4 flex items-start justify-between gap-2 sidebar-header-row">
          <Link
            to="/dashboard"
            className="block min-w-0 flex-1 sidebar-header-brand"
            data-testid="brand-link"
            aria-label="CPG Institute by BrandVault — Dashboard"
          >
            {/* Logo has a transparent background and bright pink/purple
                letters, so it reads cleanly against the dark navy shell.
                `w-full max-w-[190px]` keeps the artwork legible without
                spilling past the sidebar rail on any breakpoint. */}
            <img
              src="/brand/cpg-logo.png"
              alt="CPG Institute — by BrandVault 360"
              data-testid="brand-logo"
              className="block w-full max-w-[190px] h-auto select-none"
              draggable={false}
            />
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/60 mt-3">Member workspace</p>
          </Link>
          {/* Compact circular mark shown in the collapsed rail only.
              Sits in the same top slot the wordmark occupies in the
              expanded state so branding remains present across states.
              Hidden by index.css when `[data-collapsed="false"]`. */}
          <Link
            to="/dashboard"
            className="hidden sidebar-header-mark shrink-0"
            data-testid="brand-mark-link"
            aria-label="CPG Institute — Dashboard"
          >
            <img
              src="/brand/short-logo.png"
              alt="CPG"
              data-testid="brand-mark"
              className="block w-8 h-8 select-none"
              draggable={false}
            />
          </Link>
          <button
            onClick={toggleCollapsed}
            data-testid="sidebar-collapse"
            aria-label={iconOnly ? "Expand sidebar" : "Collapse sidebar"}
            title={iconOnly ? "Expand sidebar" : "Collapse sidebar"}
            // Visible at md+ (was xl+): tablet now uses the same
            // manual toggle as desktop. `w-9 h-9` outer hit target
            // with `w-6 h-6` icon inside makes it visibly larger than
            // the standard nav icons (w-4) so it reads as a control.
            // Color states per brand:
            //   • default → text-primary (brand pink)
            //   • hover / active / focus → text-sky-400 (brand blue)
            // No other colors introduced. The `active:` variant fires
            // during click for the pressed state.
            className={[
              "hidden md:inline-flex items-center justify-center shrink-0",
              "w-9 h-9 rounded-md",
              "text-primary hover:text-sky-400 active:text-sky-400 focus-visible:text-sky-400",
              "hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50",
              "transition-colors duration-150",
            ].join(" ")}
          >
            {iconOnly
              ? <PanelLeftOpen className="w-6 h-6" strokeWidth={2.2} />
              : <PanelLeftClose className="w-6 h-6" strokeWidth={2.2} />}
          </button>
        </div>

        <nav
          ref={navScrollRef}
          onScroll={handleNavScroll}
          className="dark-scrollbar px-3 flex-1 overflow-y-auto flex flex-col gap-6 pb-4 sidebar-nav-scroll"
        >
          {visibleSections.map((section) => (
            <div key={section.id} data-testid={`sidebar-${section.id}`} className="sidebar-section-block">
              <p className="px-3 text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2 sidebar-section-label">
                {section.label}
              </p>
              <div className="flex flex-col gap-0.5">
                {(itemsBySection[section.id] || []).map((item) => (
                  <SidebarItem key={item.id} item={item} ctx={rendererCtx} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-4 mt-auto border-t border-white/10 sidebar-feedback-wrap">
          <button
            type="button"
            data-testid="sidebar-share-feedback-btn"
            onClick={() => window.dispatchEvent(new CustomEvent("cpg:open-feedback"))}
            className="btn-primary w-full text-sm hover:brightness-110 hover:-translate-y-0.5 transition-all"
            title="Share your feedback"
          >
            <MessageSquarePlus className="w-4 h-4" />
            <span className="sidebar-feedback-label">Share Your Feedback</span>
          </button>
        </div>
      </aside>

      <main
        className={[
          "flex-1 min-w-0 flex flex-col",
          // Reserve left space:
          //   • Mobile (<md): off-canvas → pl-0
          //   • Tablet (md-xl): ALWAYS pl-[68px] — the tablet overlay
          //     floats above content without reflowing it, so padding
          //     stays pinned to the rail width even when the overlay
          //     is open.
          //   • Desktop (≥xl): docked width follows `collapsed` and
          //     content reflows to match (push/reflow behavior).
          "md:pl-[68px]",
          collapsed ? "xl:pl-[68px]" : "xl:pl-72",
          "transition-[padding] duration-200",
        ].join(" ")}
      >
        <header
          ref={headerRef}
          data-testid="app-header"
          // Fixed instead of sticky — html/body/#root have `overflow-x:
          // hidden` (see index.css) which turns them into implicit scroll
          // containers and defeats `sticky` on descendants. Fixed sidesteps
          // that quirk and lets the header stay pinned across mobile,
          // tablet, and desktop. Pairs with `padding-top: var(--header-h)`
          // on the content region so nothing hides beneath it.
          className={[
            "fixed top-0 right-0 border-b backdrop-blur z-[35]",
            // <md (mobile only): dark shell background so header reads
            // as one with the mobile drawer overlay. ≥md (tablet +
            // desktop): light surface docked next to the persistent
            // sidebar.
            "bg-shell-side text-shell-side-fg border-shell-side",
            "md:bg-white/90 md:text-foreground md:border-shell-border",
            // Anchor the header's LEFT edge to the CURRENTLY VISIBLE
            // sidebar footprint:
            //   • Mobile (<md): sidebar off-canvas → left-0
            //   • Tablet (md-xl): ALWAYS md:left-[68px] — the overlay
            //     floats above content without pushing the header.
            //   • Desktop (≥xl): follows `collapsed` push/reflow.
            "left-0 md:left-[68px]",
            collapsed ? "xl:left-[68px]" : "xl:left-72",
            "transition-[left] duration-200",
          ].join(" ")}
        >
          <div className="w-full flex items-center justify-between gap-2 md:gap-3 px-3 md:px-6 lg:px-10 py-3 md:py-4">
            <button
              onClick={() => setMobileNavOpen((v) => !v)}
              data-testid="topbar-menu"
              aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileNavOpen ? "true" : "false"}
              // Hamburger opens the off-canvas drawer on MOBILE only.
              // From md+ the sidebar is persistent and the manual
              // toggle inside it is the single affordance — no
              // hamburger, no scrim, no overlay.
              className="md:hidden p-2 rounded text-white hover:bg-white/10 shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            {/* Mobile brand lockup (<md only) — logo mirrors the
                sidebar header so both surfaces read as one system on
                the phone. From md+ the persistent sidebar rail already
                carries the icon-rail representation, and the header
                switches to the page-title context. */}
            <Link
              to="/dashboard"
              className="md:hidden flex items-center gap-2 min-w-0 flex-1"
              data-testid="topbar-brand"
              aria-label="CPG Institute — go to dashboard"
            >
              <img
                src="/brand/cpg-logo.png"
                alt="CPG Institute"
                className="h-8 md:h-9 w-auto select-none"
                draggable={false}
              />
            </Link>
            <h2 className="hidden md:block font-semibold text-lg lg:text-xl truncate flex-1 md:flex-none min-w-0">{resolvedTitle}</h2>
            {/* Global search placement:
                • lg+ (≥1025 px): inline in the top row, full width up to 28rem.
                • md   (768–1024 px): inline compact between the logo and
                                     the action icons so tablets never need
                                     a magnifier tap.
                • <md  (<768 px):    magnifier button in the top row that
                                     toggles a full-width overlay row below. */}
            {!disabledSet.has("header-search") && (
              <div className="flex-1 hidden md:block max-w-md lg:max-w-md" data-testid="header-search">
                <GlobalSearch />
              </div>
            )}
            <div className="flex items-center gap-0.5 md:gap-2 shrink-0">
              {!disabledSet.has("header-search") && (
                <button
                  type="button"
                  onClick={() => setMobileSearchOpen((v) => !v)}
                  className="md:hidden p-2 rounded text-white hover:bg-white/10"
                  aria-label={mobileSearchOpen ? "Close search" : "Open search"}
                  aria-expanded={mobileSearchOpen}
                  data-testid="topbar-search-toggle"
                >
                  <Search className="w-5 h-5" />
                </button>
              )}
              {!disabledSet.has("header-reading-level") && <ReadingLevelPill />}
              {!disabledSet.has("header-badge-rail") && (
                <div className="hidden lg:block" data-testid="header-badge-rail">
                  <BadgeRail certs={certs} passedSlugs={passedSlugs} primaryEarned={primaryEarned} compact />
                </div>
              )}
              {!disabledSet.has("header-community") && <CommunityButton />}
              {!disabledSet.has("header-help") && (
                <button className="hidden md:inline p-2 rounded md:text-foreground md:hover:bg-muted xl:text-foreground xl:hover:bg-muted" title="Help" data-testid="topbar-help">
                  <HelpCircle className="w-4 h-4" />
                </button>
              )}
              {!disabledSet.has("header-notifications") && <NotificationsBell />}
              {!disabledSet.has("header-package") && (
                <span className="hidden lg:inline-flex text-[10px] uppercase tracking-[0.22em] font-bold rounded-full border border-shell-border px-3 py-1" data-testid="topbar-package">
                  {pkgLabel}
                </span>
              )}
              {!disabledSet.has("header-account") && <AccountMenu />}
            </div>
          </div>
          {/* Mobile-only search overlay row — only rendered when the
              magnifier is tapped so the header stays compact by default.
              From md+ the search already lives inline in the top row. */}
          {!disabledSet.has("header-search") && mobileSearchOpen && (
            <div
              className="w-full px-3 pb-3 md:hidden"
              data-testid="header-search-mobile"
            >
              <GlobalSearch autoFocus onSubmit={() => setMobileSearchOpen(false)} />
            </div>
          )}
        </header>
        {/* Header is fixed above — push all content below it by exactly
            the header's measured height (+ a small gap), so nothing
            renders underneath at any breakpoint.
            NOTE: `overflow-x-clip` (not `-hidden`) is deliberate — it
            still visually clips any accidental horizontal overflow but
            does NOT establish a scroll container. That matters because
            per-page sticky elements (e.g. AILabShell's sub-nav) need
            to attach to the window scroll, and `overflow: hidden` on
            an axis silently promotes the div into a scroll container
            that would trap them. */}
        <div
          className="flex-1 min-w-0 pb-3 md:pb-6 lg:pb-10 overflow-x-clip"
          style={{ paddingTop: "calc(var(--header-h, 64px) + 1rem)" }}
        >
          {/* Canonical content frame — every page renders inside this
              single container so horizontal padding stays identical
              across the app. Per the responsive spec: NO centered
              max-width and NO artificial side margins at any width —
              the main content must fill the entire viewport width
              minus the currently visible sidebar footprint. Pages
              with their own `max-w-*` still render as they wish;
              this frame no longer clamps them from above. */}
          <div
            className="w-full min-w-0 px-3 md:px-6 lg:px-10"
            data-testid="app-content-frame"
          >
            {/* On any admin sub-page (but not /admin itself), render a
                back link to the Admin Console. Keeps sub-pages one tap
                away from the console menu regardless of what each page
                renders internally. */}
            {(() => {
              const p = location.pathname || "";
              const onAdminSub = p.startsWith("/admin/") && p !== "/admin";
              if (!onAdminSub) return null;
              return (
                <div className="pt-4 md:pt-6" data-testid="admin-back-link-wrap">
                  <Link
                    to="/admin"
                    className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 bg-white hover:bg-neutral-100 border border-neutral-200 rounded-full px-3 py-1.5 shadow-sm transition-colors"
                    data-testid="admin-back-link"
                  >
                    <span aria-hidden="true">←</span>
                    <span>Back to Admin Console</span>
                  </Link>
                </div>
              );
            })()}
            {children && (
              <ViewportRouteGuard
                items={layout.items}
                viewport={viewport}
              >
                {children}
              </ViewportRouteGuard>
            )}
          </div>
        </div>
      </main>
      <GuidedTour />
      <TourPickerOverlay />
      <CommunityProfileOnboardingModal />
      <AccountAssistantWidget />
      <FeedbackWidget />
    </div>
    </SidebarStateContext.Provider>
    </MenuVisibilityContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Item renderers
// ---------------------------------------------------------------------------
function SidebarItem({ item, ctx }) {
  const reg = ITEM_REGISTRY[item.id];
  if (!reg) return null;
  // Per-viewport gate — admin configured this item OFF for the caller's
  // current viewport in Menu Directory. Render as a non-navigable row
  // with a "View on Desktop" chip so members know the feature exists
  // but is intentionally big-screen-only. `nav-account` is exempt (it's
  // the guardrail out of the app on any device).
  if (item.id !== "nav-account" && viewportGated(item, ctx.viewport)) {
    return <ViewportGatedItem item={item} reg={reg} viewport={ctx.viewport} />;
  }
  // Plan-gate: item's Menu Directory `plans` list excludes this user.
  // Short-circuit ALL renderer types (dropdowns, course, modules,
  // simple items) so gated features consistently show an "Upgrade to
  // Access" chip and route to /account/subscription instead of
  // rendering their normal (possibly interactive) UI.
  if (ctx.planGated && ctx.planGated.has(item.id)) {
    return <PlanGatedItem item={item} reg={reg} />;
  }
  switch (reg.kind) {
    case "course":    return <CourseItem item={item} reg={reg} ctx={ctx} />;
    case "modules":   return <ModulesDropdown item={item} reg={reg} ctx={ctx} />;
    case "tools":     return <ToolsDropdown item={item} reg={reg} ctx={ctx} />;
    case "databases": return <DatabasesDropdown item={item} reg={reg} ctx={ctx} />;
    case "ai-studio": return <AIStudioDropdown item={item} reg={reg} ctx={ctx} />;
    default:          return <SimpleItem item={item} reg={reg} />;;
  }
}


/**
 * Non-navigable sidebar row shown when an item is switched off for the
 * caller's viewport. Chip text names the smallest viewport where it IS
 * clickable so members with a laptop nearby know to switch devices —
 * e.g. mobile blocked but tablet+desktop allowed → "View on Tablet".
 */
function ViewportGatedItem({ item, reg, viewport }) {
  const Icon = reg.icon;
  const vps = item.viewports || {};
  const priority = ["desktop", "tablet", "mobile"]; // biggest first
  const availableOn = priority.find((v) => vps[v] !== false);
  const chipLabel = availableOn
    ? `View on ${availableOn[0].toUpperCase()}${availableOn.slice(1)}`
    : "Unavailable";
  return (
    <div
      data-testid={`${item.id}-viewport-gated`}
      title={`This item is available on ${availableOn || "another device"}. You're viewing on ${viewport}.`}
      aria-disabled="true"
      className="relative flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] text-white/50 cursor-not-allowed"
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1 truncate">{item.label}</span>
      <span
        className="sidebar-item-suffix text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-shell-accent/25 text-shell-accent shrink-0"
        data-testid={`${item.id}-viewport-chip`}
      >
        {chipLabel}
      </span>
    </div>
  );
}

/**
 * PlanGatedItem — sidebar row for a menu item whose Menu Directory
 * `plans` list excludes the current user. Stays visible + navigable
 * (unlike hidden items which drop out entirely), but its click
 * reroutes to /account/subscription and it carries an "Upgrade to
 * Access" chip using the SAME gradient chip style as "Coming Soon"
 * so the two upsells share a visual language. Works for every
 * renderer kind (dropdowns, course, simple) since we short-circuit
 * before the switch in SidebarItem.
 */
function PlanGatedItem({ item, reg }) {
  const Icon = reg.icon;
  return (
    <NavLink
      to="/account/subscription"
      data-testid={item.id}
      title={`${item.label} — upgrade to access`}
      className="relative flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] transition-colors text-white/60 hover:bg-white/5 hover:text-primary"
    >
      <Icon className="w-4 h-4" />
      <span className="flex-1 truncate">{item.label}</span>
      <span
        className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-grad shrink-0 sidebar-item-suffix"
        data-testid={`${item.id}-upgrade-to-access`}
      >
        Upgrade to Access
      </span>
    </NavLink>
  );
}

/**
 * Full-page route guard — when the current pathname resolves to a
 * sidebar item whose `viewports` config is OFF for the caller's
 * current viewport, render a lock screen INSTEAD of the page. Blocks
 * the two access loopholes an admin can't otherwise plug:
 *   1. deep-linking / a shared URL on a mobile or iPad device, and
 *   2. loading on desktop then dragging DevTools to a mobile size.
 *
 * `nav-account` is exempt (guardrail out of the app) and dropdown
 * parents match every child path via NAV_ROUTE_PREFIXES so, e.g.
 * banning "Databases" on mobile also blocks `/databases/retailers`.
 */
function ViewportRouteGuard({ items, viewport, children }) {
  const loc = useLocation();
  const owning = useMemo(
    () => findOwningItemForPath(loc.pathname, items),
    [loc.pathname, items],
  );
  if (!owning) return children;
  if (owning.id === "nav-account") return children;
  if (!viewportGated(owning, viewport)) return children;

  const vps = owning.viewports || {};
  const priority = ["desktop", "tablet", "mobile"];
  const availableOn = priority.find((v) => vps[v] !== false);
  const CurrentIcon =
    viewport === "mobile" ? Smartphone : viewport === "tablet" ? Tablet : Monitor;
  const TargetIcon =
    availableOn === "mobile" ? Smartphone : availableOn === "tablet" ? Tablet : Monitor;
  const targetPretty = availableOn
    ? availableOn.charAt(0).toUpperCase() + availableOn.slice(1)
    : "another device";

  return (
    <div
      className="min-h-[60vh] grid place-items-center px-6 py-14"
      data-testid="viewport-route-guard"
    >
      <div className="max-w-md w-full text-center surface p-8 flex flex-col items-center gap-4">
        <div className="inline-flex items-center gap-2 text-muted-foreground">
          <CurrentIcon className="w-5 h-5 opacity-50" />
          <span className="text-xs uppercase tracking-widest">Not available here</span>
        </div>
        <h1 className="font-display text-2xl">Open this on {targetPretty}</h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">{owning.label || "This page"}</span>{" "}
          is set to <span className="font-semibold text-foreground">{targetPretty} only</span>{" "}
          by your admin. Reopen it on a {targetPretty.toLowerCase()} device — or resize
          your browser back to {targetPretty.toLowerCase()} width — to continue.
        </p>
        <div className="mt-1 inline-flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <CurrentIcon className="w-4 h-4" /> You're on {viewport}
          </span>
          <span aria-hidden>→</span>
          <span className="inline-flex items-center gap-1 text-primary font-semibold">
            <TargetIcon className="w-4 h-4" /> Needs {targetPretty}
          </span>
        </div>
        <Link
          // Back to Menu takes founders to /welcome (the always-open
          // entry hub). We avoid navigate(-1) here because if the user
          // came from another gated page we'd risk a second guard
          // bounce. /welcome is exempt from viewport gating by design.
          to="/welcome"
          className="mt-2 text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
          data-testid="viewport-route-guard-back"
        >
          <ChevronLeft className="w-3 h-3" /> Back to menu
        </Link>
      </div>
    </div>
  );
}

const NAV_LINK_CLASS = ({ isActive }) => [
  "relative flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] transition-colors",
  isActive ? "bg-white/5 text-primary" : "text-white/80 hover:bg-white/5 hover:text-primary",
].join(" ");

function SimpleItem({ item, reg }) {
  const Icon = reg.icon;
  // Coming Soon / New / Upgrade to Access overlays are all driven by
  // Menu Directory toggles (Admin Console → Menu Directory). Coming
  // Soon renders a non-navigable row + gradient chip; New keeps the
  // link navigable + shows a gradient chip; Upgrade to Access keeps
  // the row visible but reroutes the click to /account/subscription
  // and shows a matching gradient chip — plan gating is now a
  // funnel-friendly upsell instead of a hidden item.
  const { isComingSoon, hasNewBadge, isPlanGated } = React.useContext(MenuVisibilityContext);
  const comingSoon = isComingSoon(item.id);
  const showNew = hasNewBadge(item.id);
  const planGated = isPlanGated(item.id);

  const rowClasses = [
    "relative flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] transition-colors",
    comingSoon
      ? "text-white/50 cursor-not-allowed"
      : planGated
        ? "text-white/60 hover:bg-white/5 hover:text-primary"
        : "text-white/80 hover:bg-white/5 hover:text-primary",
  ].join(" ");

  const body = (isActive) => (
    <>
      {isActive && !comingSoon && !planGated && (
        <span className="absolute left-0 top-1 bottom-1 w-1 rounded-r-full bg-sky-400" />
      )}
      <Icon className="w-4 h-4" />
      <span className="flex-1 truncate">{item.label}</span>
      {comingSoon && (
        <span
          className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-grad shrink-0 sidebar-item-suffix"
          data-testid={`${item.id}-coming-soon`}
        >
          Coming Soon
        </span>
      )}
      {!comingSoon && planGated && (
        <span
          className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-grad shrink-0 sidebar-item-suffix"
          data-testid={`${item.id}-upgrade-to-access`}
          title="Upgrade your plan to unlock this feature"
        >
          Upgrade to Access
        </span>
      )}
      {showNew && !comingSoon && !planGated && (
        <span
          className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-grad shrink-0 sidebar-item-suffix"
          data-testid={`${item.id}-new-badge`}
        >
          New
        </span>
      )}
      {reg.badge && !showNew && !comingSoon && !planGated && (
        <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-shell-accent/90 shrink-0">
          {reg.badge}
        </span>
      )}
    </>
  );

  if (comingSoon) {
    return (
      <div
        data-testid={item.id}
        aria-disabled="true"
        title={item.label}
        className={rowClasses}
      >
        {body(false)}
      </div>
    );
  }
  if (planGated) {
    // Row is visible + navigable but reroutes to the Subscription
    // page instead of the gated feature. Never marks itself as
    // `NavLink` "active" because we're always sending members away
    // from the actual URL.
    return (
      <NavLink
        to="/account/subscription"
        data-testid={item.id}
        title={`${item.label} — upgrade to access`}
        className={rowClasses}
      >
        {body(false)}
      </NavLink>
    );
  }
  return (
    <NavLink to={reg.to} end={!!reg.end} data-testid={item.id} title={item.label} className={NAV_LINK_CLASS}>
      {({ isActive }) => body(isActive)}
    </NavLink>
  );
}

function CourseItem({ item, reg, ctx }) {
  const Icon = reg.icon;
  return (
    <NavLink to={reg.to} data-testid={item.id} title={item.label}
      className={({ isActive }) => ["relative flex flex-col gap-1 rounded-md px-3 py-2 text-[13.5px] transition-colors",
        isActive ? "bg-white/5 text-primary" : "text-white/80 hover:bg-white/5 hover:text-primary"].join(" ")}>
      {({ isActive }) => {
        // Keep the sidebar Course Overview in sync with the Dashboard's
        // "modules complete" card. Both now use completed_slugs / (core+bonus)
        // so a user can't see 10% here and 13% one screen over.
        const total = (ctx.progress?.core_total || 0) + (ctx.progress?.bonus_total || 0);
        const done  = (ctx.progress?.completed_slugs || []).length;
        const pct   = total > 0 ? Math.round((done / total) * 100) : 0;
        return (
          <>
            {isActive && <span className="absolute left-0 top-1 bottom-1 w-1 rounded-r-full bg-sky-400" />}
            <span className="flex items-center gap-3">
              <Icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
              {total > 0 && (
                <span
                  className={[
                    "sidebar-item-progress text-[10px] font-semibold tabular-nums",
                    pct >= 100 ? "text-primary" : "text-white/50",
                  ].join(" ")}
                  data-testid={`${item.id}-progress-label`}
                >
                  {pct}%
                </span>
              )}
            </span>
            {total > 0 && (
              <div
                className="sidebar-item-progress ml-7 h-1 rounded-full bg-white/10 overflow-hidden"
                data-testid={`${item.id}-progress-bar`}
                title={`${done} of ${total} modules complete`}
              >
                <div
                  className="h-full rounded-full transition-[width] duration-700 ease-out"
                  style={{
                    width: `${pct}%`,
                    background: pct >= 100
                      ? "linear-gradient(90deg, #E91E63 0%, #FBBF24 100%)"
                      : "linear-gradient(90deg, #D81B60 0%, #6A0DAD 100%)",
                  }}
                />
              </div>
            )}
          </>
        );
      }}
    </NavLink>
  );
}

function ModulesDropdown({ item, reg, ctx }) {
  const Icon = reg.icon;
  const { modules, modsOpen, setModsOpen, childrenOverrides, childrenAllowed } = ctx;
  const ov = (childrenOverrides || {})["nav-modules"] || {};
  const forbidden = new Set(((childrenAllowed || {})["nav-modules"]) || []);
  const decorated = modules
    // Plan-gated modules stay visible in the dropdown but flip to
    // "Upgrade to Access" — never filtered out. Menu Directory is
    // the single source of truth (children_allowed[nav-modules]).
    .map((m) => {
      const patch = ov[m.slug] || {};
      return { ...m, label: patch.label || m.name, disabled: !!patch.disabled,
        coming_soon: !!patch.coming_soon,
        planGated: forbidden.has(m.slug),
        order: patch.order };
    })
    .filter((m) => !m.disabled)
    .sort((a, b) => {
      if (typeof a.order === "number" && typeof b.order === "number") return a.order - b.order;
      if (typeof a.order === "number") return -1;
      if (typeof b.order === "number") return 1;
      return 0;
    });
  return (
    <>
      <button onClick={() => setModsOpen(!modsOpen)} data-testid={`${item.id}-toggle`}
        title={item.label}
        className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] text-white/80 hover:bg-white/5 hover:text-primary transition-colors">
        <Icon className="w-4 h-4" />
        <span className="flex-1 text-left">{item.label}</span>
        <span className="text-[10px] text-white/50 sidebar-item-suffix">{decorated.length}</span>
        {modsOpen ? <ChevronDown className="w-3.5 h-3.5 sidebar-item-caret" /> : <ChevronRight className="w-3.5 h-3.5 sidebar-item-caret" />}
      </button>
      {modsOpen && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/10 flex flex-col gap-0.5 sidebar-dropdown-children">
          {(() => {
            const anyOrdered = decorated.some((m) => typeof m.order === "number");
            if (anyOrdered) {
              // Custom-order flow: just render in the current sorted order,
              // dropping the Core/Bonus groupings since the admin has taken over.
              return decorated.map((m) => (
                <ModuleNavLink key={m.slug} m={m} />
              ));
            }
            return (
              <>
                {/*
                  Sidebar grouping invariant:
                  - "bonus" modules go under Bonus.
                  - EVERY other module (track = "core", missing, or any admin-
                    edited value like "Foundations") goes under Core.
                  Do NOT tighten this to `m.track === "core"` — module editors
                  can rename the `track` field freely and no rename should
                  ever remove a module from the sidebar. Regression guard for
                  the recurring "Module 1 vanished" bug.
                */}
                <p className="px-3 pt-2 text-[9px] uppercase tracking-widest text-white/40">Core (10)</p>
                {decorated.filter((m) => m.track !== "bonus").map((m) => (
                  <ModuleNavLink key={m.slug} m={m} />
                ))}
                <p className="px-3 pt-2 text-[9px] uppercase tracking-widest text-white/40">Bonus (6)</p>
                {decorated.filter((m) => m.track === "bonus").map((m) => (
                  <ModuleNavLink key={m.slug} m={m} />
                ))}
              </>
            );
          })()}
        </div>
      )}
    </>
  );
}

function ModuleNavLink({ m }) {
  if (m.coming_soon) return <ChildComingSoonRow label={m.label} testid={`nav-mod-${m.slug}`} />;
  if (m.planGated) return <ChildUpgradeRow label={m.label} testid={`nav-mod-${m.slug}`} />;
  return (
    <SidebarRowTooltip label={m.label} testid={`nav-mod-${m.slug}-tooltip`}>
      <NavLink to={`/modules/${m.slug}`} data-testid={`nav-mod-${m.slug}`}
        title={m.label}
        className={({ isActive }) => ["flex items-center gap-2 rounded-md px-3 py-1.5 text-[12.5px] transition-colors",
          isActive ? "text-primary" : "text-white/70 hover:text-primary",
          m.locked ? "opacity-70" : ""].join(" ")}>
        <span className="flex-1 truncate">{m.label}</span>
        {m.locked && <Lock className="w-3 h-3 shrink-0 opacity-70" />}
      </NavLink>
    </SidebarRowTooltip>
  );
}


/**
 * Non-navigable sidebar submenu row rendered when an admin marks a
 * dropdown child (module, tool, database, AI tool, etc.) as Coming
 * Soon in Menu Directory. Same visual language as the top-level
 * "Coming Soon" chip on parent items so members can spot both at a
 * glance without wondering why the link is dead.
 */
function ChildComingSoonRow({ label, testid }) {
  return (
    <SidebarRowTooltip label={label} testid={`${testid}-tooltip`}>
      <div
        data-testid={testid}
        aria-disabled="true"
        title={label}
        className="flex items-center gap-2 rounded-md px-3 py-1.5 text-[12.5px] text-white/50 cursor-not-allowed select-none"
      >
        <span className="flex-1 truncate">{label}</span>
        <span
          className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-shell-accent/25 text-shell-accent shrink-0"
          data-testid={`${testid}-coming-soon-chip`}
        >
          Coming Soon
        </span>
      </div>
    </SidebarRowTooltip>
  );
}


/**
 * Sidebar submenu row for a dropdown child whose Menu Directory
 * `plans` list excludes the current user. Stays visible + navigable
 * (routes to /account/subscription instead of the gated feature) and
 * carries a gradient "Upgrade to Access" chip — mirrors the top-level
 * PlanGatedItem so both surfaces share the same upsell language.
 */
function ChildUpgradeRow({ label, testid }) {
  return (
    <SidebarRowTooltip label={label} testid={`${testid}-tooltip`}>
      <NavLink
        to="/account/subscription"
        data-testid={testid}
        title={`${label} — upgrade to access`}
        className="flex items-center gap-2 rounded-md px-3 py-1.5 text-[12.5px] text-white/60 hover:text-primary transition-colors"
      >
        <span className="flex-1 truncate">{label}</span>
        <span
          className="text-[9px] uppercase tracking-widest font-bold text-grad shrink-0 sidebar-item-suffix"
          data-testid={`${testid}-upgrade-to-access`}
        >
          Upgrade
        </span>
      </NavLink>
    </SidebarRowTooltip>
  );
}


/**
 * Hover text-bubble that reveals the full label of a sidebar submenu
 * row. The row itself truncates long names to keep the sidebar tidy,
 * but on hover / keyboard focus we surface the full text in a chip that
 * floats to the right of the sidebar so it never overlaps the row.
 * CSS-only (no popover library) so it stays cheap and works even when
 * the parent tab is inactive.
 */
function SidebarRowTooltip({ label, testid, children }) {
  return (
    <div className="group relative" data-testid={testid}>
      {children}
      <span
        role="tooltip"
        aria-hidden="true"
        className={[
          "pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 z-40",
          "whitespace-nowrap rounded-lg bg-neutral-900 text-white text-[16px] font-medium",
          "px-3.5 py-2 shadow-lg ring-1 ring-white/10",
          "opacity-0 -translate-x-1 transition-all duration-150",
          "group-hover:opacity-100 group-hover:translate-x-0",
          "group-focus-within:opacity-100 group-focus-within:translate-x-0",
        ].join(" ")}
      >
        {label}
      </span>
    </div>
  );
}

function ToolsDropdown({ item, reg, ctx }) {
  const Icon = reg.icon;
  const { toolsOpen, setToolsOpen, childrenOverrides, childrenAllowed } = ctx;
  const ov = (childrenOverrides || {})["nav-tools"] || {};
  const forbidden = new Set(((childrenAllowed || {})["nav-tools"]) || []);
  // Plan-gated tools remain visible — flipped to "Upgrade" row.
  const decorated = TOOLS
    .map((t, idx) => {
      const patch = ov[t.slug] || {};
      return { ...t, label: patch.label || t.name, disabled: !!patch.disabled,
        coming_soon: !!patch.coming_soon,
        planGated: forbidden.has(t.slug),
        order: typeof patch.order === "number" ? patch.order : idx };
    })
    .filter((t) => !t.disabled)
    .sort((a, b) => a.order - b.order);
  return (
    <>
      <button
        onClick={() => setToolsOpen(!toolsOpen)}
        data-testid={`${item.id}-toggle`}
        title={item.label}
        className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] text-white/80 hover:bg-white/5 hover:text-primary transition-colors"
      >
        <Icon className="w-4 h-4" />
        <span className="flex-1 text-left">{item.label}</span>
        <span className="text-[10px] text-white/50 sidebar-item-suffix">{decorated.length}</span>
        {toolsOpen ? <ChevronDown className="w-3.5 h-3.5 sidebar-item-caret" /> : <ChevronRight className="w-3.5 h-3.5 sidebar-item-caret" />}
      </button>
      {toolsOpen && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/10 flex flex-col gap-0.5 sidebar-dropdown-children" data-testid={`${item.id}-list`}>
          <NavLink
            to="/tools" end
            data-testid={`${item.id}-overview`}
            className={({ isActive }) =>
              ["flex items-center gap-2 rounded-md px-3 py-1.5 text-[12.5px] transition-colors",
                isActive ? "text-primary" : "text-white/70 hover:text-primary"].join(" ")
            }
          >
            Financial Tools Intro
          </NavLink>
          {decorated.map((t) => {
            const ToolIcon = t.icon;
            if (t.coming_soon) {
              return (
                <ChildComingSoonRow key={t.slug} label={t.label} testid={`nav-tool-${t.slug}`} />
              );
            }
            if (t.planGated) {
              return (
                <ChildUpgradeRow key={t.slug} label={t.label} testid={`nav-tool-${t.slug}`} />
              );
            }
            return (
              <NavLink
                key={t.slug}
                to={`/tools/${t.slug}`}
                data-testid={`nav-tool-${t.slug}`}
                className={({ isActive }) =>
                  ["relative flex items-center gap-2 rounded-md px-3 py-1.5 text-[12.5px] transition-colors",
                    isActive ? "text-primary" : "text-white/70 hover:text-primary"].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-0 top-1 bottom-1 w-0.5 -ml-3 bg-sky-400" />}
                    <ToolIcon className="w-3 h-3 shrink-0 opacity-80" />
                    <span className="flex-1 truncate">{t.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </>
  );
}

function DatabasesDropdown({ item, reg, ctx }) {
  const Icon = reg.icon;
  const { dbs, dbsOpen, setDbsOpen, childrenOverrides, childrenAllowed } = ctx;
  const ov = (childrenOverrides || {})["nav-databases"] || {};
  const forbidden = new Set(((childrenAllowed || {})["nav-databases"]) || []);
  // Plan-gated databases remain visible — flipped to "Upgrade" row.
  const decorated = dbs
    .map((d, idx) => {
      const patch = ov[d.slug] || {};
      return { ...d, label: patch.label || d.name, disabled: !!patch.disabled,
        coming_soon: !!patch.coming_soon,
        planGated: forbidden.has(d.slug),
        order: typeof patch.order === "number" ? patch.order : idx };
    })
    .filter((d) => !d.disabled)
    .sort((a, b) => a.order - b.order);
  return (
    <>
      <button
        onClick={() => setDbsOpen(!dbsOpen)}
        data-testid={`${item.id}-toggle`}
        title={item.label}
        className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] text-white/80 hover:bg-white/5 hover:text-primary transition-colors"
      >
        <Icon className="w-4 h-4" />
        <span className="flex-1 text-left">{item.label}</span>
        {dbsOpen ? <ChevronDown className="w-3.5 h-3.5 sidebar-item-caret" /> : <ChevronRight className="w-3.5 h-3.5 sidebar-item-caret" />}
      </button>
      {dbsOpen && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/10 flex flex-col gap-0.5 sidebar-dropdown-children">
          <NavLink
            to="/databases"
            end
            data-testid={`${item.id}-overview`}
            className={({ isActive }) =>
              ["flex items-center gap-2 rounded-md px-3 py-1.5 text-[12.5px] transition-colors",
                isActive ? "text-primary" : "text-white/70 hover:text-primary"].join(" ")
            }
          >
            Overview
          </NavLink>
          {decorated.map((d) => {
            if (d.coming_soon) {
              return <ChildComingSoonRow key={d.slug} label={d.label} testid={`nav-db-${d.slug}`} />;
            }
            if (d.planGated) {
              return <ChildUpgradeRow key={d.slug} label={d.label} testid={`nav-db-${d.slug}`} />;
            }
            return (
              <NavLink
                key={d.slug}
                to={`/databases/${d.slug}`}
                data-testid={`nav-db-${d.slug}`}
                className={({ isActive }) =>
                  ["relative flex items-center gap-2 rounded-md px-3 py-1.5 text-[12.5px] transition-colors",
                    isActive ? "text-primary" : "text-white/70 hover:text-primary",
                    d.locked ? "opacity-70" : ""].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-0 top-1 bottom-1 w-0.5 -ml-3 bg-sky-400" />}
                    <span className="flex-1 truncate">{d.label}</span>
                    {d.locked && <Lock className="w-3 h-3 shrink-0 opacity-70" />}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </>
  );
}

function AIStudioDropdown({ item, reg, ctx }) {
  const Icon = reg.icon;
  const { aiOpen, setAiOpen, childrenOverrides, childrenAllowed } = ctx;
  const ov = (childrenOverrides || {})["nav-ai-center"] || {};
  const forbidden = new Set(((childrenAllowed || {})["nav-ai-center"]) || []);
  const decorated = AI_TOOLS
    .map((t, idx) => {
      const patch = ov[t.slug] || {};
      return {
        ...t,
        label: patch.label || t.name,
        disabled: !!patch.disabled,
        coming_soon: !!patch.coming_soon,
        planGated: forbidden.has(t.slug),
        order: typeof patch.order === "number" ? patch.order : idx,
      };
    })
    .filter((t) => !t.disabled)
    .sort((a, b) => a.order - b.order);
  return (
    <>
      <button
        onClick={() => setAiOpen(!aiOpen)}
        data-testid={`${item.id}-toggle`}
        className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] text-white/80 hover:bg-white/5 hover:text-primary transition-colors"
      >
        <Icon className="w-4 h-4" />
        <span className="flex-1 text-left">{item.label}</span>
        <span className="text-[10px] text-white/50">{decorated.length}</span>
        {aiOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {aiOpen && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/10 flex flex-col gap-0.5" data-testid={`${item.id}-list`}>
          <NavLink
            to={reg.to}
            end
            data-testid={`${item.id}-overview`}
            className={({ isActive }) =>
              ["flex items-center gap-2 rounded-md px-3 py-1.5 text-[12.5px] transition-colors",
                isActive ? "text-primary" : "text-white/70 hover:text-primary"].join(" ")
            }
          >
            AI Lab Home
          </NavLink>
          {decorated.map((t) => {
            const ToolIcon = t.icon;
            if (t.coming_soon) {
              return <ChildComingSoonRow key={t.slug} label={t.label} testid={`nav-ai-${t.slug}`} />;
            }
            if (t.planGated) {
              return <ChildUpgradeRow key={t.slug} label={t.label} testid={`nav-ai-${t.slug}`} />;
            }
            return (
              <NavLink
                key={t.slug}
                to={`/ai/${t.slug}`}
                data-testid={`nav-ai-${t.slug}`}
                className={({ isActive }) =>
                  ["relative flex items-center gap-2 rounded-md px-3 py-1.5 text-[12.5px] transition-colors",
                    isActive ? "text-primary" : "text-white/70 hover:text-primary"].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-0 top-1 bottom-1 w-0.5 -ml-3 bg-sky-400" />}
                    {ToolIcon && <ToolIcon className="w-3 h-3 shrink-0 opacity-80" />}
                    <span className="flex-1 truncate">{t.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </>
  );
}

